import { ApplicationHelpType } from '../../../utilities/Constants';
import { ConfidenceBadge } from './ConfidenceBadge';
import { DisplayMessages } from '../../../utilities/DisplayMessages';
import { Help } from '../../shared/Help';
import { LoaderAnimation } from '../../shared/Loader';
import { Stack } from 'react-bootstrap';
import { useCaseFileContext } from '../../../contexts/useCaseFileContext';
import { useGetAssessmentHasModel } from './data/useGetAssessmentHasModel';
import { useGetThreatScore } from './data/useGetThreatScore';

const confidenceHelpText = `Threat Score Confidence measures the correlation between Threat Score values and the actual case resolution value.  R/Y/G font color indicates how the threat score confidence for a given case compares to the confidence of all other open-active cases in that zone.  Red font = bottom 1/3, Yellow = middle 1/3, Green = top 1/3 of confidence values.`;
const improveConfidenceHelpText = `The following variables are currently missing in this Case File.  If these missing variables were updated, confidence would improve.  The missing factors are listed in order of having the biggest impact on the confidence score.`;

export function ThreatScore(props: { confidenceScore?: number | undefined; isReady: boolean }) {
    const caseFile = useCaseFileContext();

    const hasModel = useGetAssessmentHasModel();
    const threatScore = useGetThreatScore(props.isReady, caseFile.currentCase.guid);

    const isLoading = hasModel.isLoading || threatScore.isLoading;

    if (isLoading) {
        return <LoaderAnimation size="xs" variant="secondary" />;
    }

    const data = threatScore.data;
    const hasError = threatScore.isError;

    if (hasError) {
        return <div className="text-danger">{DisplayMessages.UnexpectedError}</div>;
    }

    return (
        <Stack>
            <div className="row">
                <div className="col-lg-4 col-md-6">
                    <div className="bold mb-2 mr-1" style={{ borderBottom: '1px solid #c7c8c9' }}>
                        Criteria that drive a higher score
                    </div>
                    <Stack direction="vertical" gap={1}>
                        {data?.higherFactors.map((item, idx) => (
                            <span key={idx}>{item}</span>
                        ))}
                    </Stack>
                </div>
                <div className="col-lg-4 col-md-6">
                    <div className="bold mb-2" style={{ borderBottom: '1px solid #c7c8c9' }}>
                        Criteria that drive a lower score
                    </div>
                    <Stack direction="vertical" gap={1}>
                        {data?.lowerFactors.map((item, idx) => (
                            <span key={idx}>{item}</span>
                        ))}
                    </Stack>
                </div>
            </div>
            {(props.confidenceScore ?? 0) > 0  && hasModel.data && (
                <Stack gap={3}>
                    <Stack
                        direction="horizontal"
                        style={{ alignItems: 'center', columnGap: '1rem' }}
                        className="mt-3"
                    >
                        <div>
                            <strong>Confidence</strong>{' '}
                            <Help
                                helpText={confidenceHelpText}
                                title="Confidence"
                                type={ApplicationHelpType.Info}
                            />
                        </div>
                        <div>
                            <ConfidenceBadge
                                confidenceRating={props.confidenceScore}
                                confidenceLevel={data?.confidenceColorLevel}
                            />
                        </div>
                    </Stack>
                    <div className="col-lg-8 col-md-8">
                        <div
                            className="bold mb-2 mr-1"
                            style={{ borderBottom: '1px solid #c7c8c9' }}
                        >
                            Additional criteria that would improve confidence
                            <Help
                                helpText={improveConfidenceHelpText}
                                title="Additional criteria that would improve confidence"
                                type={ApplicationHelpType.Info}
                            />
                        </div>
                        <Stack direction="vertical" gap={1}>
                            {data?.improveConfidenceFactors.map((item, idx) => (
                                <span key={idx}>{item}</span>
                            ))}
                        </Stack>
                    </div>
                </Stack>
            )}
        </Stack>
    );
}
